/**
 * Decode a JWT payload segment safely.
 *
 * JWTs use base64URL (`-`/`_` instead of `+`/`/`, no `=` padding), which the
 * native `atob` rejects — and `atob` alone produces a Latin-1 byte string, so
 * multibyte UTF-8 content (e.g. a Vietnamese `name`/`given_name` like "Nguyễn")
 * is either corrupted or throws. Naive `JSON.parse(atob(...))` therefore fails
 * on such tokens, which surfaces as a broken login (empty session/user).
 *
 * This normalises base64URL → base64, restores padding, then decodes the bytes
 * as UTF-8. Returns `{}` on any failure so callers stay defensive.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const seg = token.split('.')[1] ?? '';
    if (!seg) return {};
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch {
    return {};
  }
}
